import { useCallback, useEffect, useMemo, useState } from 'react';
import { Sparkles, Plus, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/Dialog';
import { usePermission } from '../../hooks/usePermission';
import { apiClient, setActiveAiWorkspaceId } from '../../lib/api-client';
import { useAuth } from '../../contexts/AuthContext';

type WorkspaceSummary = {
  id: string;
  tenantCode: string;
  displayName: string;
  description?: string;
  color?: string;
  streamCount?: number;
  roleCount?: number;
  lastRunAt?: string | null;
};

export function AiWorkspaces() {
  const navigate = useNavigate();
  const { accessibleTenants, selectedTenants } = useAuth();
  const { isAllowed: canManage } = usePermission('ai_config', 'manage');
  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    id: '',
    tenantCode: '',
    displayName: '',
    description: '',
    color: '#0EA5E9',
  });
  const [cursorSlot, setCursorSlot] = useState<'primary' | 'alternate'>('primary');
  const [alternateConfigured, setAlternateConfigured] = useState(false);
  const [alternateKey, setAlternateKey] = useState('');
  const [cursorKeyMessage, setCursorKeyMessage] = useState('');
  const [cursorKeyBusy, setCursorKeyBusy] = useState(false);

  const tenantsToQuery = useMemo(
    () => (selectedTenants.length > 0 ? selectedTenants : accessibleTenants),
    [accessibleTenants, selectedTenants],
  );
  const tenantFilterSet = useMemo(
    () => new Set(tenantsToQuery.map((tenant) => tenant.toLowerCase())),
    [tenantsToQuery],
  );
  const tenantFilterKey = useMemo(() => tenantsToQuery.join(','), [tenantsToQuery]);

  const loadWorkspaces = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.getAiWorkspaces();
      setWorkspaces(Array.isArray(res.workspaces) ? res.workspaces : []);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCursorKey = useCallback(async () => {
    try {
      const status = await apiClient.get<{ slot: 'primary' | 'alternate'; alternateConfigured: boolean }>(
        '/api/admin/ai-config/cursor-key',
      );
      setCursorSlot(status.slot === 'alternate' ? 'alternate' : 'primary');
      setAlternateConfigured(Boolean(status.alternateConfigured));
    } catch {
      setCursorKeyMessage('Cursor key slot could not be loaded.');
    }
  }, []);

  useEffect(() => {
    void loadWorkspaces();
    void loadCursorKey();
  }, [loadWorkspaces, loadCursorKey, tenantFilterKey]);

  async function saveAlternateKey() {
    const value = alternateKey.trim();
    if (!value) return;
    setCursorKeyBusy(true);
    setCursorKeyMessage('');
    try {
      const status = await apiClient.put<{ slot: 'primary' | 'alternate'; alternateConfigured: boolean }>(
        '/api/admin/ai-config/cursor-key',
        { alternateKey: value },
      );
      setAlternateKey('');
      setCursorSlot(status.slot === 'alternate' ? 'alternate' : 'primary');
      setAlternateConfigured(Boolean(status.alternateConfigured));
      setCursorKeyMessage('Alternate key saved. AI calls keep using the active plan until it is spent or you switch.');
    } catch (error: unknown) {
      setCursorKeyMessage(error instanceof Error ? error.message : 'Could not save the alternate key.');
    } finally {
      setCursorKeyBusy(false);
    }
  }

  async function switchCursorSlot(slot: 'primary' | 'alternate') {
    setCursorKeyBusy(true);
    setCursorKeyMessage('');
    try {
      const status = await apiClient.put<{ slot: 'primary' | 'alternate'; alternateConfigured: boolean }>(
        '/api/admin/ai-config/cursor-key',
        { slot },
      );
      setCursorSlot(status.slot === 'alternate' ? 'alternate' : 'primary');
      setAlternateConfigured(Boolean(status.alternateConfigured));
    } catch (error: unknown) {
      setCursorKeyMessage(error instanceof Error ? error.message : 'Could not switch the Cursor key.');
    } finally {
      setCursorKeyBusy(false);
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return workspaces.filter((workspace) => {
      if (!tenantFilterSet.has(String(workspace.tenantCode || '').toLowerCase())) {
        return false;
      }
      if (!q) return true;
      return `${workspace.displayName} ${workspace.id} ${workspace.tenantCode || ''} ${workspace.description || ''}`
        .toLowerCase()
        .includes(q);
    });
  }, [workspaces, search, tenantFilterSet]);

  function slugify(value: string): string {
    return value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40);
  }

  async function handleCreateWorkspace() {
    await apiClient.createAiWorkspace({
      id: form.id || slugify(form.displayName),
      tenantCode: form.tenantCode,
      displayName: form.displayName,
      description: form.description,
      color: form.color,
      icon: 'sparkles',
    });
    setCreateOpen(false);
    setForm({ id: '', tenantCode: '', displayName: '', description: '', color: '#0EA5E9' });
    await loadWorkspaces();
  }

  function openWorkspace(workspaceId: string) {
    setActiveAiWorkspaceId(workspaceId);
    navigate(`/admin/ai-config/${encodeURIComponent(workspaceId)}`);
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Sparkles}
        description="AI Workspaces — isolated configuration and runtime contexts."
        count={filtered.length}
        countLabel="workspaces"
        actions={(
          <div className="flex flex-wrap sm:flex-nowrap items-stretch gap-2 w-full sm:w-auto h-full">
            <Input
              placeholder="Search workspaces..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              wrapperClassName="sm:w-56 shrink-0"
              className="h-full"
            />
            {canManage && (
              <Button className="h-full shrink-0" onClick={() => setCreateOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Create Workspace
              </Button>
            )}
          </div>
        )}
      />

      <Card>
        <CardContent className="space-y-3 p-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Cursor API key</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Active plan: {cursorSlot === 'alternate' ? 'alternate' : 'primary'}
              {alternateConfigured ? '. Alternate key is saved.' : '. No alternate key saved.'}
              {' '}When the active plan's included allowance is spent, every Cursor agent call switches to the other key and stays on it.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant={cursorSlot === 'primary' ? 'default' : 'outline'}
              disabled={!canManage || cursorKeyBusy}
              onClick={() => void switchCursorSlot('primary')}
            >
              Primary
            </Button>
            <Button
              variant={cursorSlot === 'alternate' ? 'default' : 'outline'}
              disabled={!canManage || cursorKeyBusy || !alternateConfigured}
              onClick={() => void switchCursorSlot('alternate')}
            >
              Alternate
            </Button>
          </div>
          {canManage && (
            <div className="flex flex-wrap items-center gap-2">
              <Input
                type="password"
                placeholder="Alternate Cursor API key"
                value={alternateKey}
                onChange={(e) => setAlternateKey(e.target.value)}
                wrapperClassName="sm:w-80"
                autoComplete="off"
              />
              <Button disabled={cursorKeyBusy || !alternateKey.trim()} onClick={() => void saveAlternateKey()}>
                Save alternate key
              </Button>
            </div>
          )}
          {cursorKeyMessage && (
            <p className="text-sm text-gray-600 dark:text-gray-300">{cursorKeyMessage}</p>
          )}
        </CardContent>
      </Card>

      {loading ? (
        <Card>
          <CardContent className="py-10 text-sm text-gray-500 dark:text-gray-400">Loading AI workspaces...</CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((workspace) => (
            <Card key={workspace.id} className="overflow-hidden">
              <div className="h-1.5 w-full" style={{ backgroundColor: workspace.color || '#0EA5E9' }} />
              <CardContent className="space-y-3 p-4">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{workspace.id}</p>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{workspace.displayName}</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Tenant: {workspace.tenantCode}</p>
                  <p className="line-clamp-2 text-sm text-gray-600 dark:text-gray-300">{workspace.description || 'No description'}</p>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs text-gray-600 dark:text-gray-300">
                  <div>
                    <p className="text-gray-500">Streams</p>
                    <p className="font-semibold">{workspace.streamCount ?? 0}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Roles</p>
                    <p className="font-semibold">{workspace.roleCount ?? 0}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Last run</p>
                    <p className="font-semibold">{workspace.lastRunAt ? new Date(workspace.lastRunAt).toLocaleDateString() : '-'}</p>
                  </div>
                </div>
                <Button variant="outline" className="w-full" onClick={() => openWorkspace(workspace.id)}>
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Open
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create AI Workspace</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-3">
            <Input
              placeholder="Display name"
              value={form.displayName}
              onChange={(e) => {
                const displayName = e.target.value;
                setForm((prev) => ({ ...prev, displayName, id: prev.id || slugify(displayName) }));
              }}
            />
            <Input
              placeholder="workspace-id"
              value={form.id}
              onChange={(e) => setForm((prev) => ({ ...prev, id: slugify(e.target.value) }))}
            />
            <div className="space-y-1">
              <label htmlFor="workspace-tenant" className="text-xs font-medium text-gray-700 dark:text-gray-300">
                Tenant
              </label>
              <select
                id="workspace-tenant"
                value={form.tenantCode}
                onChange={(e) => setForm((prev) => ({ ...prev, tenantCode: e.target.value }))}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                required
              >
                <option value="">Select tenant</option>
                {accessibleTenants.map((tenantCode) => (
                  <option key={tenantCode} value={tenantCode}>
                    {tenantCode}
                  </option>
                ))}
              </select>
            </div>
            <Input
              placeholder="Description"
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
            />
            <Input
              type="color"
              value={form.color}
              onChange={(e) => setForm((prev) => ({ ...prev, color: e.target.value }))}
            />
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button
              onClick={handleCreateWorkspace}
              disabled={!form.tenantCode || !form.displayName || !(form.id || slugify(form.displayName))}
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
